import { HttpClient, HttpBackend } from '@angular/common/http';
import { inject } from '@angular/core';

import { TranslateLoader, TranslationObject } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Apollo } from 'apollo-angular';

import { createBaseTranslateLoader } from '@resident-nx/shared';

// Resolve dependencies within the factory to ensure a valid injection context

/**
 * Creates a TranslateLoader for web applications
 */
export function createWebTranslateLoader(
  http?: HttpClient,
  handler?: HttpBackend,
  apollo?: Apollo
): TranslateLoader {
  // If services aren't provided, get them from DI within the factory context
  const httpClient = http ?? inject(HttpClient);
  const httpBackend = handler ?? inject(HttpBackend);
  const apolloClient = apollo ?? inject(Apollo);

  if (!httpClient) {
    throw new Error('HttpClient is required');
  }

  if (!httpBackend) {
    throw new Error('HttpBackend is required');
  }

  if (!apolloClient) {
    throw new Error('Apollo service is required');
  }

  // Create a new HttpClient that bypasses interceptors for translation files
  const translationHttpClient = new HttpClient(httpBackend);

  return createBaseTranslateLoader(
    {
      loadLocalTranslations: (locale: string): Observable<TranslationObject> => {
        // In v17, TranslateHttpLoader no longer accepts constructor parameters
        // Use direct HTTP call instead with the same default paths
        const app$ = translationHttpClient.get<TranslationObject>(`/assets/i18n/${locale}.json`);

        // `@immomio/ui` ships its own strings (aria-labels, pagination, ...) which the build
        // copies to /im-lib-i18n. Their keys are unprefixed, which is exactly how the library
        // components look them up.
        // NOTE: deliberately not under /assets — the whole of apps/web-saas/src/assets is
        // copied onto that path and would clobber a sibling glob writing into it.
        const designLib$ = translationHttpClient
          .get<TranslationObject>(`/im-lib-i18n/${locale}.json`)
          .pipe(catchError(() => of({} as TranslationObject)));

        // Design-library strings sit lowest: the app's own JSON may override them,
        // and Phrase overrides both (see createBaseTranslateLoader).
        return forkJoin([app$, designLib$]).pipe(
          map(([app, designLib]) => ({ ...designLib, ...app }))
        );
      },
    },
    apolloClient
  );
}
